import { useState } from 'react';

interface RepeatableListInputProps {
  label: string;
  items: string[];
  onChange: (items: string[]) => void;
  maxItems?: number;
  placeholder?: string;
}

export function RepeatableListInput({
  label,
  items,
  onChange,
  maxItems = 10,
  placeholder = 'Add item...',
}: RepeatableListInputProps) {
  const [newItem, setNewItem] = useState('');

  const addItem = () => {
    const trimmed = newItem.trim();
    if (!trimmed || items.length >= maxItems) return;
    onChange([...items, trimmed]);
    setNewItem('');
  };

  const removeItem = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, value: string) => {
    const updated = [...items];
    updated[index] = value;
    onChange(updated);
  };

  const moveItem = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    const updated = [...items];
    [updated[index], updated[target]] = [updated[target], updated[index]];
    onChange(updated);
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>

      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-2">
          <span className="text-xs text-gray-400 w-5 text-right">{index + 1}.</span>
          <input
            type="text"
            value={item}
            onChange={(e) => updateItem(index, e.target.value)}
            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => moveItem(index, -1)}
              disabled={index === 0}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-xs px-1"
              title="Move up"
            >
              ▲
            </button>
            <button
              type="button"
              onClick={() => moveItem(index, 1)}
              disabled={index === items.length - 1}
              className="text-gray-400 hover:text-gray-600 disabled:opacity-30 text-xs px-1"
              title="Move down"
            >
              ▼
            </button>
          </div>
          <button
            type="button"
            onClick={() => removeItem(index)}
            className="text-red-500 hover:text-red-700 text-sm px-2"
          >
            ×
          </button>
        </div>
      ))}

      {items.length < maxItems && (
        <div className="flex items-center gap-2">
          <span className="w-5" />
          <input
            type="text"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addItem())}
            placeholder={placeholder}
            className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <button
            type="button"
            onClick={addItem}
            className="text-green-600 hover:text-green-800 text-sm font-medium px-3 py-1.5 border border-green-300 rounded-md"
          >
            Add
          </button>
        </div>
      )}

      {maxItems < Infinity && (
        <p className="text-xs text-gray-400">
          {items.length}/{maxItems} items
        </p>
      )}
    </div>
  );
}
